import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Play,
  ShieldCheck,
  MessageSquareText,
  Users,
  Sparkles,
  Zap,
  BadgeCheck,
  Volume2,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../lib/auth";

/* --------------------------------- assets --------------------------------- */

const HERO_VIDEOS = {
  support: {
    src: "https://assets.mixkit.co/videos/preview/mixkit-young-mother-talking-to-the-camera-4657-large.mp4",
    poster: "https://images.unsplash.com/photo-1715610258704-e8f9f5710fe0?w=800&q=80&auto=format",
    speaker: "@meera_iyer",
    quote: "UBI protects people from the tech transition. It's overdue.",
  },
  oppose: {
    src: "https://assets.mixkit.co/videos/preview/mixkit-serious-man-speaking-to-the-camera-40808-large.mp4",
    poster: "https://images.unsplash.com/photo-1546872006-42c78c0ccb29?w=800&q=80&auto=format",
    speaker: "@dr_kumar",
    quote: "UBI collapses labor participation — history proves it.",
  },
};

const AVATARS = [
  "https://images.unsplash.com/photo-1778692258270-bc0e80e975c0?w=200&q=80&auto=format",
  "https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=200&q=80&auto=format",
  "https://images.pexels.com/photos/7580822/pexels-photo-7580822.jpeg?auto=compress&cs=tinysrgb&h=200&w=200",
  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&q=80&auto=format",
  "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=200&q=80&auto=format",
  "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=200&q=80&auto=format",
];

// Red→Purple gradient tokens
const GRAD = "bg-gradient-to-r from-[#ef4444] via-[#c026d3] to-[#a855f7]";
const GRAD_TEXT = "bg-gradient-to-r from-[#ef4444] via-[#c026d3] to-[#a855f7] bg-clip-text text-transparent";

/* -------------------------------- helpers -------------------------------- */

function Counter({ to, suffix = "", className = "", duration = 1400 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const step = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(to * eased));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [to, duration]);
  return (
    <span ref={ref} className={className}>
      {n.toLocaleString("en-IN")}{suffix}
    </span>
  );
}

function Waveform({ tone = "support" }) {
  const color = tone === "support" ? "bg-emerald-500" : "bg-rose-500";
  return (
    <div className="flex items-end gap-[3px] h-4">
      {[10, 18, 8, 22, 12, 20, 6, 16, 22, 14, 8, 18].map((h, i) => (
        <span
          key={i}
          className={`${color} w-[3px] rounded-sm`}
          style={{
            height: `${h}px`,
            animation: `waveform 1.${(i % 6) + 2}s ease-in-out ${i * 0.08}s infinite alternate`,
          }}
        />
      ))}
    </div>
  );
}

function DebateVideoCard({ tone, data, delay = 0 }) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const isSupport = tone === "support";
  const stanceCls = isSupport ? "bg-emerald-500" : "bg-rose-500";
  const glow = isSupport
    ? "shadow-[0_20px_80px_-20px_rgba(16,185,129,0.35)]"
    : "shadow-[0_20px_80px_-20px_rgba(244,63,94,0.35)]";

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playsInline = true; v.muted = true;
    v.play().catch(() => { /* autoplay may be blocked */ });
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-[28px] overflow-hidden border border-slate-200 bg-slate-900 ${glow}`}
      data-testid={`hero-video-${tone}`}
    >
      <div className="relative aspect-[9/14] w-full">
        <img
          src={data.poster} alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${ready ? "opacity-0" : "opacity-100"}`}
        />
        <video
          ref={videoRef} src={data.src} poster={data.poster}
          autoPlay muted loop playsInline onCanPlay={() => setReady(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className={`absolute top-4 left-4 ${stanceCls} text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full`}>
          {isSupport ? "Support" : "Oppose"}
        </div>
        <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
          <Volume2 className="w-4 h-4 text-white/60" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">{data.speaker}</div>
            <Waveform tone={tone} />
          </div>
          <p className="font-serif text-lg text-white leading-snug">
            &ldquo;{data.quote}&rdquo;
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/* --------------------------------- page ---------------------------------- */

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900 relative overflow-x-hidden">
      <style>{`
        @keyframes waveform { from { transform: scaleY(0.4); } to { transform: scaleY(1); } }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .landing-grain { background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>"); }
        @media (prefers-reduced-motion: reduce) { .float-anim { animation: none !important; } }
      `}</style>

      {/* Grain */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.05] mix-blend-multiply landing-grain z-[1]" />

      {/* Ambient gradient orbs (soft) */}
      <div className="pointer-events-none absolute -left-40 top-40 w-[520px] h-[520px] rounded-full bg-[#ef4444]/15 blur-[140px] z-0" />
      <div className="pointer-events-none absolute -right-40 top-96 w-[520px] h-[520px] rounded-full bg-[#a855f7]/15 blur-[140px] z-0" />
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[38rem] w-[720px] h-[720px] rounded-full bg-[#c026d3]/10 blur-[160px] z-0" />

      {/* Header */}
      <header className={`fixed inset-x-0 top-0 z-40 transition-colors ${scrolled ? "bg-white/80 backdrop-blur-xl border-b border-slate-200" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="font-serif text-2xl tracking-tight" data-testid="landing-brand">
            <span className={GRAD_TEXT}>Truebate</span><span className="text-slate-900">.</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.2em] text-slate-500 font-mono">
            <a href="#how" className="hover:text-slate-900 transition-colors">How it works</a>
            <a href="#proof" className="hover:text-slate-900 transition-colors">Community</a>
            <a href="#features" className="hover:text-slate-900 transition-colors">Features</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <button
                onClick={() => navigate("/home")}
                className={`group inline-flex items-center gap-2 rounded-full ${GRAD} text-white px-5 py-2.5 text-sm font-semibold hover:opacity-95 transition-opacity`}
                data-testid="landing-open-app"
              >
                Open App <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : (
              <>
                <Link to="/auth" className="text-xs uppercase tracking-[0.2em] text-slate-600 hover:text-slate-900 font-mono" data-testid="landing-signin">
                  Sign in
                </Link>
                <button
                  onClick={() => navigate("/auth")}
                  className={`rounded-full ${GRAD} text-white px-5 py-2.5 text-sm font-semibold hover:opacity-95 transition-opacity`}
                  data-testid="landing-signup"
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 pt-40 lg:pt-48 pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
              <div className="inline-flex items-center gap-2 border border-slate-200 bg-white/70 backdrop-blur rounded-full px-3 py-1 mb-8 text-[10px] font-mono uppercase tracking-[0.24em] text-slate-600">
                <Sparkles className="w-3 h-3 text-[#c026d3]" /> Vol. 01 — Verified debate network
              </div>
              <h1 className="font-serif tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl mb-8">
                <span className="block">Two sides.</span>
                <span className="block">
                  <span className="text-emerald-600">One voice</span>
                  <span className="text-slate-400"> vs. </span>
                  <span className="text-rose-600">another.</span>
                </span>
                <span className={`block italic ${GRAD_TEXT}`}>In 30 seconds.</span>
              </h1>
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed mb-10">
                TRUEBATE is a short-video arena for structured Support-vs-Oppose debates.
                Every voice here is Aadhaar-verified. No trolls. No anonymity.
                Just real people making real arguments — one clip at a time.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate("/auth")}
                  className={`group inline-flex items-center gap-2 rounded-full ${GRAD} text-white px-7 py-4 text-sm font-semibold shadow-[0_10px_40px_-10px_rgba(192,38,211,0.55)] hover:shadow-[0_16px_50px_-8px_rgba(192,38,211,0.7)] transition-shadow`}
                  data-testid="hero-start-debating"
                >
                  Start debating <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  onClick={() => navigate("/home")}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-900 px-7 py-4 text-sm font-semibold bg-white hover:bg-slate-50 transition-colors"
                  data-testid="hero-browse"
                >
                  <Play className="w-4 h-4" /> Browse debates
                </button>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {AVATARS.slice(0, 4).map((a, i) => (
                    <img key={i} src={a} alt="" className="w-8 h-8 rounded-full border-2 border-[#FAFAF7] object-cover" />
                  ))}
                </div>
                <div className="text-xs text-slate-600">
                  Joined by <span className="text-slate-900 font-semibold">2,400+</span> verified debaters this month
                </div>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="grid grid-cols-2 gap-4 sm:gap-6 relative">
              <div className="float-anim" style={{ animation: "float 6s ease-in-out infinite" }}>
                <DebateVideoCard tone="support" data={HERO_VIDEOS.support} />
              </div>
              <div className="float-anim mt-12" style={{ animation: "float 6s ease-in-out 1.5s infinite" }}>
                <DebateVideoCard tone="oppose" data={HERO_VIDEOS.oppose} delay={0.15} />
              </div>
            </div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div className={`w-14 h-14 rounded-full ${GRAD} text-white flex items-center justify-center border-4 border-[#FAFAF7] font-serif italic text-lg font-bold shadow-xl`}>
                vs
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 flex justify-center">
          <a href="#proof" className="text-slate-400 hover:text-slate-900 transition-colors flex flex-col items-center gap-1" data-testid="hero-scroll-cue">
            <span className="text-[10px] uppercase tracking-[0.3em] font-mono">Scroll</span>
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </a>
        </div>
      </section>

      {/* Live marquee ticker */}
      <div className="relative z-10 border-y border-slate-200 bg-white/60 backdrop-blur py-4 overflow-hidden">
        <div className="flex whitespace-nowrap" style={{ animation: "marquee 40s linear infinite" }}>
          {[...Array(2)].flatMap((_, dup) => [
            "Live now: 42 debates trending",
            "New voices join every 3 minutes",
            "127 opinions posted today",
            "Support 51% · Oppose 49%",
            "1,200 verified reply threads active",
            "83% of debates resolved with dialogue",
          ].map((s, i) => (
            <div key={`${dup}-${i}`} className="flex items-center gap-8 px-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600">{s}</span>
            </div>
          )))}
        </div>
      </div>

      {/* Social proof */}
      <section id="proof" className="relative z-10 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7 }}
              className="md:col-span-7 rounded-[28px] border border-slate-200 bg-white p-10 lg:p-12 relative overflow-hidden shadow-sm"
              data-testid="proof-verified"
            >
              <div className="text-[10px] uppercase font-mono tracking-[0.24em] text-slate-500 mb-4">The community</div>
              <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-8 text-slate-900">
                <Counter to={12480} className={GRAD_TEXT} />+ verified<br />
                debaters. Zero bots.
              </h2>
              <p className="text-slate-600 max-w-md mb-10 leading-relaxed">
                Every account on TRUEBATE passes Aadhaar/DigiLocker verification.
                When you argue here, you argue with a real citizen — not a burner.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  {AVATARS.map((a, i) => (
                    <img
                      key={i} src={a} alt=""
                      className="w-12 h-12 rounded-full border-[3px] border-white object-cover hover:scale-110 hover:z-10 transition-transform relative shadow"
                      style={{ zIndex: AVATARS.length - i }}
                    />
                  ))}
                  <div className={`w-12 h-12 rounded-full border-[3px] border-white ${GRAD} text-white font-bold text-[11px] flex items-center justify-center relative`}>
                    +12k
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2 text-xs text-slate-600">
                  <BadgeCheck className="w-4 h-4 fill-[#c026d3] text-white" />
                  All Aadhaar-verified
                </div>
              </div>
              <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-[#a855f7]/15 blur-3xl" />
            </motion.div>

            <div className="md:col-span-5 grid gap-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.1 }}
                className="rounded-[28px] border border-slate-200 bg-white p-8 relative overflow-hidden shadow-sm"
                data-testid="proof-debates"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[10px] uppercase font-mono tracking-[0.24em] text-slate-500">Active debates</div>
                  <MessageSquareText className="w-5 h-5 text-slate-400" />
                </div>
                <div className="font-serif text-5xl lg:text-6xl text-slate-900">
                  <Counter to={842} />
                </div>
                <div className="mt-3 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest">
                  <span className="text-emerald-600">↑ 27%</span>
                  <span className="text-slate-400">this week</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="rounded-[28px] border border-slate-200 bg-white p-8 relative overflow-hidden shadow-sm"
                data-testid="proof-opinions"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[10px] uppercase font-mono tracking-[0.24em] text-slate-500">Opinions today</div>
                  <Zap className="w-5 h-5 text-slate-400" />
                </div>
                <div className="font-serif text-5xl lg:text-6xl text-slate-900">
                  <Counter to={3910} />
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: "51%" }} />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">51 / 49 split</div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10 py-24 lg:py-32 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-2xl">
            <div className="text-[10px] uppercase font-mono tracking-[0.24em] text-slate-500 mb-4">How it works</div>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[0.95] tracking-tight text-slate-900">
              Three moves.<br />
              <span className={`italic ${GRAD_TEXT}`}>That&apos;s the whole game.</span>
            </h2>
          </div>

          <div className="grid gap-6">
            {[
              { num: "01", title: "Record 30 seconds", copy: "Camera or upload. Say what you actually think in the time it takes to sip chai. AI auto-generates your title and thumbnail.", accent: "#c026d3" },
              { num: "02", title: "Pick a side", copy: "Green for Support. Red for Oppose. Every debate is a two-column argument — no fence-sitting, no false balance.", accent: "#10b981" },
              { num: "03", title: "Get replied to. By humans.", copy: "Verified citizens on the other side film a reply. Threads inherit stance color. Nuance in seconds — not screenshots.", accent: "#f43f5e" },
            ].map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative rounded-3xl border border-slate-200 bg-white p-8 lg:p-12 grid md:grid-cols-12 gap-8 items-center hover:border-slate-400 hover:shadow-lg transition-all"
                data-testid={`step-${step.num}`}
              >
                <div className="md:col-span-3">
                  <span
                    className="font-serif text-[110px] lg:text-[140px] leading-none opacity-25 group-hover:opacity-50 transition-opacity"
                    style={{ color: step.accent }}
                  >
                    {step.num}
                  </span>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-serif text-3xl lg:text-4xl mb-4 text-slate-900">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed max-w-2xl">{step.copy}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="relative z-10 py-24 lg:py-32 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-2xl">
            <div className="text-[10px] uppercase font-mono tracking-[0.24em] text-slate-500 mb-4">What makes it different</div>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[0.95] tracking-tight text-slate-900">
              Not another feed.<br />
              <span className={`italic ${GRAD_TEXT}`}>A structured arena.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-12 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7 }}
              className="md:col-span-8 md:row-span-2 rounded-[28px] border border-slate-200 bg-gradient-to-br from-[#ef4444]/[0.06] to-[#a855f7]/[0.08] p-10 lg:p-14 group hover:border-[#c026d3]/40 hover:shadow-xl transition-all"
              data-testid="feature-verified"
            >
              <div className={`inline-flex w-14 h-14 rounded-2xl ${GRAD} items-center justify-center mb-8`}>
                <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-4xl lg:text-5xl mb-6 leading-tight text-slate-900">
                Every voice, Aadhaar-verified.
              </h3>
              <p className="text-slate-600 leading-relaxed max-w-lg">
                DigiLocker or Aadhaar-linked verification is required before you can post.
                Browse for free — but when you speak, you speak as a real citizen. No burner accounts.
                No engagement farms. No bad-faith bots.
              </p>
              <div className="mt-8 flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-slate-500">
                <BadgeCheck className="w-4 h-4 fill-[#c026d3] text-white" />
                &lt; 30 seconds to verify
              </div>
            </motion.div>

            {[
              { icon: MessageSquareText, title: "Support · Oppose", copy: "Every reply inherits a stance. Green vs Red — visible at a glance.", tone: "text-emerald-600", bg: "bg-emerald-500/10", testid: "feature-stance" },
              { icon: Zap, title: "30-second cap", copy: "No rants. No monologues. Say it sharp, or don't say it.", tone: "text-rose-600", bg: "bg-rose-500/10", testid: "feature-shortform" },
              { icon: Users, title: "Communities", copy: "Private or public groups. Restrict a debate to a trusted circle.", tone: "text-[#c026d3]", bg: "bg-[#c026d3]/10", testid: "feature-groups" },
              { icon: Sparkles, title: "AI on your team", copy: "Auto-thumbnails and description generation via Whisper + Claude.", tone: "text-[#a855f7]", bg: "bg-[#a855f7]/10", testid: "feature-ai" },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
                className="md:col-span-4 rounded-[28px] border border-slate-200 bg-white p-8 hover:border-slate-400 hover:shadow-md transition-all"
                data-testid={f.testid}
              >
                <div className={`inline-flex w-11 h-11 rounded-xl ${f.bg} items-center justify-center mb-6`}>
                  <f.icon className={`w-6 h-6 ${f.tone}`} strokeWidth={1.75} />
                </div>
                <h3 className="font-serif text-2xl mb-3 text-slate-900">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 py-24 lg:py-32 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-2xl">
            <div className="text-[10px] uppercase font-mono tracking-[0.24em] text-slate-500 mb-4">Voices on TRUEBATE</div>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[0.95] tracking-tight text-slate-900">
              Not a feed of takes.<br/>
              <span className={`italic ${GRAD_TEXT}`}>A record of arguments.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { stance: "support", quote: "Finally, a platform where I know the person arguing with me is a real citizen. Debate feels earned again.", who: "@meera_iyer", role: "Policy analyst, Bangalore", avatar: AVATARS[2] },
              { stance: "oppose", quote: "The 30-second cap forces me to be sharp. If you can't say it in 30, you don't understand it well enough.", who: "@aakash_desai", role: "Independent journalist, Mumbai", avatar: AVATARS[0] },
              { stance: "support", quote: "Green vs red is such a small design decision. But it changed how I read every reply on this app.", who: "@priya_e", role: "Economist, Delhi", avatar: AVATARS[3] },
              { stance: "oppose", quote: "I don't want another engagement-farm timeline. TRUEBATE fixes that by making every reply take a stance.", who: "@ravi_menon", role: "Product designer, Chennai", avatar: AVATARS[1] },
            ].map((t, i) => {
              const stanceCls = t.stance === "support"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-rose-100 text-rose-700";
              return (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10 relative shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`testimonial-${i}`}
                >
                  <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] mb-6 ${stanceCls}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${t.stance === "support" ? "bg-emerald-600" : "bg-rose-600"}`} />
                    {t.stance}
                  </div>
                  <blockquote className="font-serif text-2xl lg:text-3xl leading-snug mb-8 text-slate-900">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="flex items-center gap-3">
                    <img src={t.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="text-sm font-semibold flex items-center gap-1 text-slate-900">
                        {t.who}
                        <BadgeCheck className="w-3.5 h-3.5 fill-[#c026d3] text-white" />
                      </div>
                      <div className="text-xs text-slate-500">{t.role}</div>
                    </div>
                  </figcaption>
                </motion.figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-24 lg:py-32 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-[10px] uppercase font-mono tracking-[0.24em] text-slate-500 mb-6">The floor is open</div>
          <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] tracking-tight mb-10 text-slate-900">
            Pick a side.<br />
            <span className={`italic ${GRAD_TEXT}`}>Say it in 30.</span>
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate("/auth")}
              className={`group inline-flex items-center gap-2 rounded-full ${GRAD} text-white px-8 py-4 text-sm font-semibold shadow-[0_10px_50px_-10px_rgba(192,38,211,0.6)] hover:shadow-[0_16px_60px_-8px_rgba(192,38,211,0.8)] transition-shadow`}
              data-testid="footer-cta-signup"
            >
              Get verified — join now <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={() => navigate("/home")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-900 px-8 py-4 text-sm font-semibold bg-white hover:bg-slate-50 transition-colors"
              data-testid="footer-cta-browse"
            >
              <Play className="w-4 h-4" /> Watch a debate
            </button>
          </div>
          <div className="text-xs text-slate-500 mt-8">
            Free forever. Aadhaar verification takes under 30 seconds.
          </div>
        </div>
      </section>

      {/* Massive footer wordmark */}
      <footer className="relative z-10 pt-16 pb-12 border-t border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-6 mb-16">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">© 2026 TRUEBATE — All rights reserved</div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Built for verified discourse</div>
        </div>
        <div className="text-center leading-none select-none pointer-events-none">
          <div className={`font-serif text-[24vw] tracking-tighter opacity-[0.08] ${GRAD_TEXT}`} data-testid="footer-wordmark">
            Truebate.
          </div>
        </div>
      </footer>
    </div>
  );
}
